import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import { useCurrentUser } from '@sdir/auth/lib/hooks';
import { AlertBox, DataTable, Loader, SdirButton, SdirModal } from '@sdir/sds';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { BackLink } from '@sdir/blueprint.aps/lib/components';
import { endorsementApi } from '../../httpclient';
import { ReporteesTableHeader } from '../../helpers/TableHeaders';
import AddReporteeModal, { AddReporteeInput } from '../Molecules/AddReporteeModal';
import { routes } from '../Routes';

const UserManagementPage: React.FC = () => {
  const intl = useIntl();
  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showRenewConfirmation, setShowRenewConfirmation] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [emailToDelete, setEmailToDelete] = useState<string>();

  const {
    result: invitations,
    errorObject: invitationsError,
    loading: invitationsLoading,
    callApi: getInvitations,
  } = useApi({
    apiFunction: () => endorsementApi.getInvitations(),
  });

  const {
    errorObject: addError,
    loading: addLoading,
    callApi: addReportee,
  } = useApi({
    apiFunction: (payload) => endorsementApi.addReportee(payload),
    successCallback: () => {
      setShowAddModal(false);
      getInvitations();
    },
  });

  const {
    errorObject: removeError,
    loading: removeLoading,
    callApi: removeReportee,
  } = useApi({
    apiFunction: (payload) => endorsementApi.removeReportee(payload),
    successCallback: () => {
      setShowDeleteModal(false);
      getInvitations();
    },
  });

  const {
    errorObject: renewError,
    loading: renewLoading,
    callApi: renewInvite,
  } = useApi({
    apiFunction: (id) => endorsementApi.renewReporteeInvite(id),
    successCallback: () => {
      setShowRenewConfirmation(true);
    },
  });

  const formattedData = useMemo(() => {
    return (
      invitations?.map((invite) => {
        return {
          note: invite?.note || '',
          email: invite?.email || '',
          // Status: Active | Accepted | Expired
          status: intl.formatMessage({ id: `usermanagement.table.status.${invite?.state}` }),
          actions: (
            <ActionsWrapper>
              {invite?.email && (
                <SdirButton
                  text={intl.formatMessage({ id: 'general.remove' })}
                  onClick={() => onRemove(invite?.email || '')}
                  size="sm"
                />
              )}
              {invite?.id && invite?.state !== 'Accepted' && (
                <SdirButton
                  text={intl.formatMessage({ id: 'usermanagement.button.renew' })}
                  onClick={() => onRenew(invite?.id || '')}
                  size="sm"
                />
              )}
            </ActionsWrapper>
          ),
        };
      }) || []
    );
  }, [invitations]);

  const onAdd = useCallback(() => {
    setShowAddModal(true);
  }, []);

  const onAddSubmit = useCallback((values: AddReporteeInput) => {
    addReportee(values);
  }, []);

  const onRemove = useCallback((email: string) => {
    setEmailToDelete(email);
    setShowDeleteModal(true);
  }, []);

  const onRemoveConfirm = useCallback(() => {
    removeReportee({ email: emailToDelete });
  }, [emailToDelete]);

  const onRenew = useCallback((id: string) => {
    renewInvite(id);
  }, []);

  const handleExitClick = () => navigate(routes.home);

  useEffect(() => {
    if (currentUser?.profile?.reportee) {
      getInvitations();
    }
  }, [currentUser?.profile?.reportee]);

  const showEmpty = formattedData.length === 0 && !invitationsLoading;
  const loading = removeLoading || invitationsLoading || renewLoading;

  return (
    <>
      <BackLink
        text={intl.formatMessage({ id: 'general.backtoapplications' })}
        onClick={handleExitClick}
      />
      <Header>
        <h1>{intl.formatMessage({ id: 'usermanagement.title' })}</h1>
        <div>
          {loading && <Loader />}
          {!showEmpty && !loading && (
            <SdirButton
              type="primary"
              text={intl.formatMessage({ id: 'usermanagement.button.add' })}
              onClick={onAdd}
            />
          )}
        </div>
      </Header>
      {invitationsError && <ErrorMessage error={invitationsError} />}
      {formattedData?.length > 0 && (
        <TableWrapper>
          <StyledDataTable
            className="reportees-table"
            columns={ReporteesTableHeader}
            data={formattedData}
            sortableKeys="note,email,status"
          />
        </TableWrapper>
      )}
      {showEmpty && (
        <AlertBox
          type="discinfo"
          title={intl.formatMessage({ id: 'usermanagement.empty.title' })}
          text={intl.formatMessage({ id: 'usermanagement.empty.description' })}
          hideClose
          extraContent={
            <EmptyAction>
              <SdirButton
                type="primary"
                text={intl.formatMessage({ id: 'usermanagement.button.add' })}
                onClick={onAdd}
              />
            </EmptyAction>
          }
        />
      )}
      <AddReporteeModal
        visible={showAddModal}
        loading={addLoading}
        error={addError}
        onCancel={() => setShowAddModal(false)}
        onConfirm={onAddSubmit}
      />
      <SdirModal
        visible={showRenewConfirmation}
        onOK={() => setShowRenewConfirmation(false)}
        okText="OK"
        width="400px"
      >
        <h2>Renewal was successful</h2>
        <p>The user has now received a new email with instructions on how to proceed.</p>
      </SdirModal>
      <SdirModal
        visible={showDeleteModal}
        onOK={onRemoveConfirm}
        onCancel={() => setShowDeleteModal(false)}
        okText={intl.formatMessage({ id: 'general.yes' })}
        cancelText={intl.formatMessage({ id: 'general.cancel' })}
        width="400px"
      >
        <h2>Delete this invitation</h2>
        <p>Are you sure you want to delete this invitation?</p>
      </SdirModal>
    </>
  );
};

export default UserManagementPage;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 10px;

  & > div {
    display: flex;

    button {
      margin-left: 15px;
    }
  }
`;

const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  min-width: 190px;

  button {
    width: 90px;
  }
`;

const TableWrapper = styled.div`
  background-color: #ffffff;
  border: 1px solid ${({ theme }) => theme.colors.primary8};
  border-radius: 6px;
  padding: 0 30px;

  thead tr th button {
    padding-left: 0;
  }
`;

const StyledDataTable = styled(DataTable)`
  background-color: transparent;
  tbody tr {
    border-top: 1px solid ${({ theme }) => theme.colors.primary8};
    height: 40px;

    &:last-child {
      border-bottom: 1px solid ${({ theme }) => theme.colors.primary8};
    }
  }
`;

const EmptyAction = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 30px 0;
`;
